import { useEffect, useState } from "react";
import { api, currency, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const TYPE_OPTIONS = [
  { value: "part", label: "Part" },
  { value: "supply", label: "Supply" },
  { value: "turbo_timing", label: "Turbo Timing Job" },
  { value: "na_timing", label: "NA Timing Job" },
];

export default function Alerts() {
  const [lowStock, setLowStock] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/parts", { params: { low_stock: true } }),
        api.get("/suppliers").catch(() => ({ data: [] })),
      ]);
      setLowStock(p.data);
      setSuppliers(s.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (p) => setEditing({ ...p });

  const onSave = async (e) => {
    e.preventDefault();
    if (!editing?.id) return;
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        sku: editing.sku,
        type: editing.type,
        quantity: Number(editing.quantity),
        min_stock: Number(editing.min_stock),
        unit_cost: Number(editing.unit_cost),
        supplier: editing.supplier || "",
        category: editing.category || "",
        notes: editing.notes || "",
      };
      await api.patch(`/parts/${editing.id}`, payload);
      toast.success("Part updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-6xl">
      <PageHeader
        eyebrow="Reorder"
        title="Reorder Alerts"
        description="Parts at or below the minimum stock level. Click a part name to view or edit its details."
      />
      <div className="mt-6 bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {["Part", "SKU", "In Stock", "Min", "Deficit", "Unit Cost", "Reorder Cost", "Supplier"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {loading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-zinc-500">Loading...</td></tr>
            )}
            {!loading && lowStock.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <div className="inline-flex items-center gap-2 text-sm text-emerald-700 font-semibold">
                    <CheckCircle2 className="w-4 h-4" /> All parts are adequately stocked.
                  </div>
                </td>
              </tr>
            )}
            {lowStock.map((p) => {
              const deficit = Math.max(p.min_stock - p.quantity, 0);
              return (
                <tr
                  key={p.id}
                  className="hover:bg-zinc-50 cursor-pointer"
                  data-testid={`alert-row-${p.sku}`}
                  onClick={() => openEdit(p)}
                >
                  <td className="px-4 py-3 text-sm font-semibold text-zinc-950">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                      className="flex items-center gap-2 hover:text-[#002FA7] hover:underline"
                      data-testid={`open-part-${p.sku}`}
                      title="Click to view or edit part"
                    >
                      <AlertTriangle className="w-4 h-4 text-[#FF2400]" />
                      {p.name}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-zinc-700">{p.sku}</td>
                  <td className="px-4 py-3 text-sm font-mono font-bold text-[#FF2400]">{p.quantity}</td>
                  <td className="px-4 py-3 text-sm font-mono text-zinc-700">{p.min_stock}</td>
                  <td className="px-4 py-3 text-sm font-mono font-bold text-zinc-900">{deficit}</td>
                  <td className="px-4 py-3 text-sm font-mono text-zinc-900">{currency(p.unit_cost)}</td>
                  <td className="px-4 py-3 text-sm font-mono text-zinc-900">{currency(deficit * p.unit_cost)}</td>
                  <td className="px-4 py-3 text-sm text-zinc-700">{p.supplier || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-200 flex-shrink-0">
            <DialogTitle className="font-heading text-2xl tracking-tight">
              {editing?.name || "Edit part"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={onSave} className="flex flex-col flex-1 min-h-0" data-testid="alert-edit-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 py-4 overflow-y-auto flex-1">
                <div className="col-span-2">
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Name</Label>
                  <Input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="alert-name-input" className="rounded-sm border-zinc-300" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">SKU</Label>
                  <Input required value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} data-testid="alert-sku-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Type</Label>
                  <Select value={editing.type || "part"} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300" data-testid="alert-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Quantity</Label>
                  <Input type="number" required value={editing.quantity} onChange={(e) => setEditing({ ...editing, quantity: e.target.value })} data-testid="alert-quantity-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Min Stock</Label>
                  <Input type="number" required value={editing.min_stock} onChange={(e) => setEditing({ ...editing, min_stock: e.target.value })} data-testid="alert-min-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Unit Cost</Label>
                  <Input type="number" step="0.01" required value={editing.unit_cost} onChange={(e) => setEditing({ ...editing, unit_cost: e.target.value })} data-testid="alert-cost-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Supplier</Label>
                  <Input
                    list="alert-suppliers"
                    value={editing.supplier || ""}
                    onChange={(e) => setEditing({ ...editing, supplier: e.target.value })}
                    data-testid="alert-supplier-input"
                    className="rounded-sm border-zinc-300"
                  />
                  <datalist id="alert-suppliers">
                    {suppliers.map((s) => (<option key={s.id || s.name} value={s.name} />))}
                  </datalist>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Category</Label>
                  <Input value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="alert-category-input" className="rounded-sm border-zinc-300" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Notes</Label>
                  <Textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="alert-notes-input" className="rounded-sm border-zinc-300" />
                </div>
              </div>
              <DialogFooter className="px-6 py-4 border-t border-zinc-200 bg-white flex-shrink-0">
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="alert-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                  {saving ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
